import type { Source } from "@/lib/api/catalogue";
import type { Application } from "@/lib/api/envoy";
import { SOURCE_NAME } from "@/lib/applications";

/**
 * One company with one vendor: every board it has there. The name is the
 * part of a board's slug before its first dot, as the catalogue reads it:
 * a Workday slug is the company, the server and the site.
 */
export interface CompanyRef {
    source: Source;
    company: string;
}

/** The longest name the catalogue's search and envoy take, `MAX_TERM_CHARS`. */
export const MOST_COMPANY_CHARS = 120;

// A control character, as Rust's `char::is_control` reads one: C0, DEL, C1.
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/u;

/**
 * Why envoy would refuse a company's name, already trimmed and in lower
 * case, or null when it takes it: a dot, a control character, or more than
 * 120 characters, counted as envoy counts them, by code point.
 */
export function companyRefusal(company: string): string | null {
    if ([...company].length > MOST_COMPANY_CHARS)
        return `a company is at most ${MOST_COMPANY_CHARS} characters`;
    if (company.includes(".")) return "a company has no dot";
    if (CONTROL.test(company)) return "a company has no control characters";
    return null;
}

/** The company a posting is from. */
export function companyOf(posting: { source: Source; board: string }): CompanyRef {
    return { source: posting.source, company: posting.board.split(".")[0] ?? posting.board };
}

/**
 * Whether an application is at the company: its posting's vendor is the
 * company's, and its board's slug up to the first dot is the company's
 * name, as the catalogue and envoy read one. One whose board is not known
 * is no company's.
 */
export function atCompany(
    application: Pick<Application, "posting_source" | "posting_board">,
    company: CompanyRef,
): boolean {
    const board = application.posting_board;
    if (!application.posting_source || !board) return false;
    return (
        application.posting_source === company.source &&
        (board.split(".")[0] ?? board) === company.company
    );
}

/** All of a company's jobs, on a page of their own. */
export function companyPath({ source, company }: CompanyRef): string {
    return `/jobs/company/${source.toLowerCase()}/${encodeURIComponent(company)}`;
}

/** The company an address names. Null for one no company could have. */
export function readCompany(params: { source?: string; company?: string }): CompanyRef | null {
    const source = params.source?.toUpperCase();
    const company = params.company?.trim().toLowerCase();
    if (!source || !company || !Object.hasOwn(SOURCE_NAME, source)) return null;
    if (companyRefusal(company) !== null) return null;
    return { source: source as Source, company };
}
